import React, { useEffect, useState } from 'react';
import { db } from '../services/firebase';
import { collection, getDocs, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';

interface Project {
    id: string;
    title: string;
    description: string;
    technologies: string[];
    github?: string;
    demo?: string;
    image?: string;
    category: 'core' | 'side';
    order: number;
}

const AdminProjectsPage: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'projects'), orderBy('order', 'asc'));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
            })) as Project[];
            setProjects(data);
        } catch (err) {
            setError('Failed to fetch projects.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;
        try {
            await deleteDoc(doc(db, 'projects', id));
            setProjects((prev) => prev.filter((p) => p.id !== id));
        } catch (err) {
            setError('Failed to delete project.');
        }
    };

    return (
        <div className="mx-auto max-w-3xl md:border-x">
            <div className="px-4 py-12">
                <button
                    onClick={() => navigate('/admin')}
                    className="mb-6 text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Dashboard
                </button>

                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-2xl font-bold text-foreground">Projects</h1>
                    <button
                        onClick={() => navigate('/admin/projects/new')}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-accent transition-colors"
                    >
                        <Plus size={14} />
                        New Project
                    </button>
                </div>

                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground" />
                    </div>
                ) : projects.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-12">
                        No projects yet. Create your first one!
                    </p>
                ) : (
                    <div className="space-y-1">
                        {projects.map((project) => (
                            <div
                                key={project.id}
                                className="flex items-center justify-between py-4 screen-line-after"
                            >
                                <div className="flex-1">
                                    <h3 className="text-sm font-semibold text-foreground">{project.title}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-xs font-mono text-muted-foreground">
                                            {project.category}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            · order: {project.order}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => navigate(`/admin/projects/edit/${project.id}`)}
                                        className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                                        title="Edit"
                                    >
                                        <Edit size={14} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(project.id)}
                                        className="p-2 text-muted-foreground hover:text-red-500 transition-colors"
                                        title="Delete"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminProjectsPage;
